<template>
    <DCard
        padding
    >
        <DTable
            :items="accountingProducts"
            :fields="getFields"
            hover
            sorter
        >
            <template #actions="{item}">
                <td>
                    <div style="display: flex; grid-gap: 10px;">
                        <DButton
                            :id="`edit_${item.id}`"
                            icon="pencil"
                            mini
                            @click="$inertia.get(editAccountingProductLink(item.id))"
                        />
                        <DButton
                            v-if="item.is_intercompany === false"
                            :id="`make_default_${item.id}`"
                            mini
                            icon="check-circle-outline"
                            :label="__('messages.actions.makeDefault')"
                            :disabled="item.is_default || isInLoadingState"
                            @click="markDefault(item.id)"
                        />
                        <DButton
                            v-if="item.is_intercompany"
                            :id="`make_intercompany_default_${item.id}`"
                            mini
                            icon="check-circle-outline"
                            :label="__('settings.accounting_products.index.table.make_intercompany_default')"
                            :disabled="item.is_intercompany_default || isInLoadingState"
                            @click="markIntercompanyDefault(item.id)"
                        />
                        <DButton
                            v-if="item.products.length === 0 && !item.is_default && !item.is_intercompany_default"
                            :id="`btn-delete-${item.id}`"
                            icon="trash-can-outline"
                            color="danger"
                            mini
                            :disabled="isInLoadingState"
                            @click="deleteAccountingProduct(item.id)"
                        />
                        <DButton
                            v-if="item.products.length > 0"
                            :id="`btn-open-list-modal-${item.id}`"
                            icon="eye"
                            mini
                            @click="openListOfProductsModal(item)"
                        />
                    </div>
                </td>
            </template>
            <template #is_default="{item}">
                <td>
                    <DBadge :color="item.is_default ? 'success' : 'danger'">
                        {{ item.is_default ? __('settings.accounting_products.index.table.is_default_yes') : __('settings.accounting_products.index.table.is_default_no') }}
                    </DBadge>
                </td>
            </template>
            <template #is_intercompany="{item}">
                <td>
                    <DBadge :color="item.is_intercompany ? 'success' : 'danger'">
                        {{ item.is_intercompany ? __('settings.accounting_products.index.table.is_intercompany_yes') : __('settings.accounting_products.index.table.is_intercompany_no') }}
                    </DBadge>
                </td>
            </template>
            <template #is_intercompany_default="{item}">
                <td>
                    <DBadge :color="item.is_intercompany_default ? 'success' : 'danger'">
                        {{ item.is_intercompany_default ? __('settings.accounting_products.index.table.is_intercompany_default_yes') : __('settings.accounting_products.index.table.is_intercompany_default_no') }}
                    </DBadge>
                </td>
            </template>
        </DTable>

        <DFabs>
            <DFab
                id="btn-add"
                icon="plus"
                :label="__('settings.accounting_products.index.add_accounting_products_button')"
                @click="$inertia.get($route('settings.accounting-products.create'))"
            />
        </DFabs>

        <DModal
            v-model:show="showConfirmationModal"
            :title="__('settings.accounting_products.index.delete_confirm_modal.title')"
            :message="__('settings.accounting_products.index.delete_confirm_modal.message')"
            is-confirm
            is-error
            @close="showConfirmationModal = false"
            @confirm="deleteCallBack"
        />
        <ListOfProductsModal
            :show="showListOfProductsModal"
            :accounting-product="accountingProduct"
            @close-modal="showListOfProductsModal = false"
        />
    </DCard>
</template>

<script>
import ListOfProductsModal from '~@/Components/Settings/AccountingProduct/ListOfProductsModal.vue'
export default {
    name: 'AccountingProductIndex',
    components: {
        ListOfProductsModal
    },
    props: {
        accountingProducts: {
            type: Array,
            default: () => { return [] }
        },
        isFinanceSystemNetsuite: {
            type: Boolean,
            default: () => { return false }
        }
    },
    data () {
        return {
            showConfirmationModal: false,
            deleteCallBack: () => { return {} },
            showListOfProductsModal: false,
            products: null,
            accountingProduct: null
        }
    },
    computed: {
        getFields () {
            if (this.isFinanceSystemNetsuite === true) {
                return [
                    { key: 'id', label: this.__('settings.accounting_products.index.table.id'), _classes: 'sorter-id' },
                    { key: 'name', label: this.__('settings.accounting_products.index.table.name'), _classes: 'sorter-name' },
                    { key: 'netsuite_item_id', label: this.__('settings.accounting_products.index.table.netsuite_item_id'), _classes: 'sorter-netsuite-item-id' },
                    { key: 'netsuite_department_id', label: this.__('settings.accounting_products.index.table.netsuite_department_id'), _classes: 'sorter-netsuite-item-id' },
                    { key: 'is_default', label: this.__('settings.accounting_products.index.table.is_default'), _classes: 'sorter-is-default' },
                    { key: 'is_intercompany', label: this.__('settings.accounting_products.index.table.is_intercompany'), _classes: 'sorter-intercompany' },
                    { key: 'is_intercompany_default', label: this.__('settings.accounting_products.index.table.is_intercompany_default'), _classes: 'sorter-intercompany-default' },
                    { key: 'actions', label: '', sorter: false, filter: false }
                ]
            }

            return [
                { key: 'id', label: this.__('settings.accounting_products.index.table.id'), _classes: 'sorter-id' },
                { key: 'name', label: this.__('settings.accounting_products.index.table.name'), _classes: 'sorter-name' },
                { key: 'external_cost_object_id', label: this.__('settings.accounting_products.index.table.external_cost_object_id'), _classes: 'sorter-external-cost-object-id' },
                { key: 'external_cost_center_id', label: this.__('settings.accounting_products.index.table.external_cost_center_id'), _classes: 'sorter-external-cost-center-id' },
                { key: 'is_default', label: this.__('settings.accounting_products.index.table.is_default'), _classes: 'sorter-is-default' },
                { key: 'is_intercompany', label: this.__('settings.accounting_products.index.table.is_intercompany'), _classes: 'sorter-intercompany' },
                { key: 'is_intercompany_default', label: this.__('settings.accounting_products.index.table.is_intercompany_default'), _classes: 'sorter-intercompany-default' },
                { key: 'actions', label: '', sorter: false, filter: false }
            ]
        }
    },
    methods: {
        editAccountingProductLink (id) {
            return this.$route('settings.accounting-products.edit', id)
        },
        deleteAccountingProduct (id) {
            this.deleteCallBack = () => {
                this.$inertia.delete(this.$route('settings.accounting-products.destroy', id))
                this.showConfirmationModal = false
            }
            this.showConfirmationModal = true
        },
        openListOfProductsModal (value) {
            this.accountingProduct = value
            this.showListOfProductsModal = true
        },
        markDefault (id) {
            this.$inertia.patch(this.$route('settings.accounting-products.mark-as-default', id))
        },
        markIntercompanyDefault (id) {
            this.$inertia.patch(this.$route('settings.accounting-products.mark-as-intercompany-default', id))
        }
    }
}
</script>
